import { httpsCallable } from 'firebase/functions'
import { toast } from 'sonner'
import { fbt } from 'fbtee'
import { functions } from './firebase'
import { runMutation } from './firestoreMutation'
import { track, type AnalyticsEvents } from './analytics'

const callUnlinkGroupEvent = httpsCallable<{ groupEventId: string }, { ok: true }>(functions, 'unlinkGroupEvent')

export type GroupEventUnlinkEntryPoint = AnalyticsEvents['group_event_unlinked']['entry_point']

const callableCode = (err: unknown) => (err as { code?: string } | null)?.code?.replace(/^functions\//, '')

/**
 * Unlink an event from a group through the unlinkGroupEvent callable, which
 * also clears the pair's totem slots and suggestion. The callable refuses a
 * non-admin who didn't make the link or whose link others already reacted to;
 * those get an explanatory toast instead of the generic error. Resolves to
 * whether the link was removed.
 */
export async function unlinkGroupEvent(
  ge: { id: string; groupId: string; eventId: string },
  entryPoint: GroupEventUnlinkEntryPoint,
): Promise<boolean> {
  try {
    await callUnlinkGroupEvent({ groupEventId: ge.id })
  } catch (err) {
    const code = callableCode(err)
    if (code === 'failed-precondition') {
      toast.error(fbt(
        'Others in the group are interested — ask an admin to unlink it',
        'Toast when a non-admin tries to unlink an event other group members already marked interest in',
      ))
      return false
    }
    if (code === 'permission-denied') {
      toast.error(fbt(
        'Only group admins can unlink this event',
        'Toast when a non-admin tries to unlink an event someone else linked to the group',
      ))
      return false
    }
    return runMutation(fbt("Couldn't unlink event", 'Error toast when unlinking an event from a group fails'), () => Promise.reject(err))
  }
  track('group_event_unlinked', { group_id: ge.groupId, event_id: ge.eventId, entry_point: entryPoint })
  return true
}
